/**
 * "Add a user" panel, shared by the clinic and store screens.
 *
 * The operator types an email and the user is added — no invitation to send on,
 * no token to copy. If the account already exists it simply gains the
 * membership; if not, one is created and waits for that person's first sign-in.
 *
 * Adding an address does not let anyone in. The identity provider still decides
 * who that address belongs to, so the worst a typo costs is an unused row.
 *
 * A dialog rather than a panel that pushes the member list down the page. What
 * it asks for is a person and what they may reach.
 *
 * The two roles are named and not explained. A list of what each one grants
 * was here and came out again: it repeated on every open, for a choice between
 * two options an operator makes constantly, and three lines of prose to read
 * every time is a cost paid by the person who already knows the answer. The
 * option labels carry the distinction that matters - 全店舗 against 担当店舗のみ.
 */
import { roleOptions, type TenantRoleName } from '../roles.ts';
import { useState, type FormEvent } from 'react';
import { useRevalidator } from 'react-router';
import { ApiError, post } from '../api.ts';
import { Field } from './EditPanel.tsx';
import { Modal } from './Modal.tsx';
import { StoreChecklist } from './StoreChecklist.tsx';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../../../shared/password-policy.ts';
import { Select } from './Select.tsx';

type Role = TenantRoleName;

interface AddResult {
  userId: string;
  /** True when this created the account rather than reusing an existing one. */
  created: boolean;
}

export function AddMember({
  clinicId,
  open,
  onClose,
  /** Fixed store: the store screen adds only to itself. */
  storeId,
  stores = [],
}: {
  clinicId: string;
  open: boolean;
  onClose: () => void;
  storeId?: string;
  stores?: Array<{ id: string; name: string }>;
}) {
  const revalidator = useRevalidator();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>(storeId ? 'STORE_USER' : 'CLINIC_ADMIN');
  /*
   * Several stores, because one person covering two branches is ordinary and
   * the schema has always allowed it. The store screen passes a fixed store and
   * this collapses to that one.
   */
  const [chosen, setChosen] = useState<string[]>(storeId ? [storeId] : []);
  /*
   * A password to start them off with, optional and usually blank.
   *
   * The JMC user screen has asked for one since it was written and this dialog
   * did not, so the same act - adding somebody to a clinic - took different
   * details depending on which screen it was done from, and an account added
   * here could not sign in at all without Google.
   */
  const [password, setPassword] = useState('');

  /*
   * The store screen adds to itself and offers no choice; everywhere else the
   * operator ticks the branches. Nothing is preselected: a default that lands
   * on whichever store sorted first is an access decision made by an accident
   * of ordering.
   */
  const effectiveStores = storeId ? [storeId] : chosen;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AddResult | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      if (role !== 'CLINIC_ADMIN' && effectiveStores.length === 0) {
        // Caught here so the operator sees the reason, rather than a rejected
        // request naming a field they cannot see.
        setError('担当店舗を1つ以上選んでください。店舗が未登録の場合は先に店舗を追加します。');
        return;
      }

      const created = await post<AddResult>(`/admin/clinics/${clinicId}/members`, {
        email,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        role,
        ...(role !== 'CLINIC_ADMIN' ? { storeIds: effectiveStores } : {}),
        // Omitted when blank rather than sent empty: the schema's minimum would
        // refuse '' and blank is the normal answer.
        ...(password ? { password } : {}),
      });
      setResult(created);
      setEmail('');
      setDisplayName('');
      setPassword('');
      if (!storeId) setChosen([]);
      await revalidator.revalidate();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? [err.message, ...err.fields].join(' / ')
          : '追加できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        setResult(null);
        setError(null);
        onClose();
      }}
      title="ユーザーを追加"
      caption="メールアドレスでアカウントを作成または紐付けします。ログインできるのは本人だけです。"
    >
      <form onSubmit={(event) => void submit(event)}>
        <div className="editpanel__body">
        <Field
          label="メールアドレス"
          htmlFor="m-email"
          required
          hint="アカウントがなければ作成します。ログインできるのは本人だけです。"
        >
          <input
            id="m-email"
            type="email"
            required
            maxLength={320}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {/*
          A name, so the clinic's list is people rather than addresses. Only
          used when this creates the account: an existing user's name is theirs
          to change, and overwriting it from another clinic's screen is not this
          dialog's business.
        */}
        <Field
          label="氏名"
          htmlFor="m-name"
          hint="新しくアカウントを作成する場合に使います。空欄ならメールアドレスから作られます。"
        >
          <input
            id="m-name"
            maxLength={200}
            value={displayName}
            placeholder="例：山田 花子"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        <Field
          label="パスワード（任意）"
          htmlFor="m-password"
          hint={`空欄ならGoogleログインのみになります。${PASSWORD_HINT}。あとからご本人がプロフィール画面で変更できます。`}
        >
          <input
            id="m-password"
            type="password"
            autoComplete="new-password"
            value={password}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={200}
            placeholder={PASSWORD_HINT}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {/*
          On the store page too now, minus 「クリニック管理者」: adding somebody to
          ONE store can make them its store admin or its staff.
        */}
        <Field label="権限" htmlFor="m-role">
          <Select
            id="m-role"
            value={role}
            onChange={(next) => setRole(next as Role)}
            options={roleOptions(!storeId)}
          />
        </Field>

        {!storeId && role !== 'CLINIC_ADMIN' && (
          <StoreChecklist idPrefix="m-stores" stores={stores} value={chosen} onChange={setChosen} />
        )}
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {result && (
        <p className="notice" role="status">
          <strong>ユーザーを追加しました。</strong>
          <br />
          {result.created
            ? 'アカウントを作成しました。本人がこのメールアドレスでログインすると利用できます。'
            : '既存アカウントに権限を付与しました。次回のログインから反映されます。'}
        </p>
      )}

      <div className="editpanel__actions">
        <button className="btn" type="submit" disabled={saving}>
          {saving ? '追加中…' : '追加する'}
        </button>
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => {
            setResult(null);
            setError(null);
            onClose();
          }}
          disabled={saving}
        >
          閉じる
        </button>
      </div>
      </form>
    </Modal>
  );
}
